import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, ArrowLeft, TrendingUp, FileText, Calendar, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AnalyticsPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [doctorId]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/${doctorId}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5C7063]">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
          <Button
            onClick={() => navigate(`/dashboard/${doctorId}`)}
            variant="ghost"
            size="sm"
            className="rounded-full mr-4"
            data-testid="analytics-back-btn"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <Stethoscope className="w-8 h-8 text-[#2C5F44] mr-2" strokeWidth={1.5} />
          <span className="text-2xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Analytics Dashboard
          </span>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Performance Overview
          </h1>
          <p className="text-lg text-[#5C7063]">
            Track your content creation and engagement
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
            data-testid="analytics-total-scripts"
          >
            <div className="w-12 h-12 bg-[#82A08B]/20 rounded-2xl flex items-center justify-center mb-4">
              <FileText className="w-6 h-6 text-[#2C5F44]" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-[#5C7063] mb-1">Total Scripts</p>
            <p className="text-3xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {analytics?.total_scripts || 0}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
            data-testid="analytics-this-month"
          >
            <div className="w-12 h-12 bg-[#82A08B]/20 rounded-2xl flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-[#2C5F44]" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-[#5C7063] mb-1">This Month</p>
            <p className="text-3xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {analytics?.scripts_this_month || 0}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10"
            data-testid="analytics-scheduled"
          >
            <div className="w-12 h-12 bg-[#82A08B]/20 rounded-2xl flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-[#2C5F44]" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-[#5C7063] mb-1">Scheduled</p>
            <p className="text-3xl font-bold text-[#2C5F44]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {analytics?.scheduled_posts || 0}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-gradient-to-br from-[#2C5F44] to-[#244b36] rounded-3xl p-8 card-hover text-white"
            data-testid="analytics-completed"
          >
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-white/80 mb-1">Published</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {analytics?.completed_posts || 0}
            </p>
          </motion.div>
        </div>

        {/* Compliance Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10 mb-12"
        >
          <h2 className="text-2xl font-bold text-[#1C2A21] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Compliance Overview
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                {analytics?.compliance_breakdown?.Low || 0}
              </div>
              <p className="text-sm text-[#5C7063]">Low Risk</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-600 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                {analytics?.compliance_breakdown?.Medium || 0}
              </div>
              <p className="text-sm text-[#5C7063]">Medium Risk</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                {analytics?.compliance_breakdown?.High || 0}
              </div>
              <p className="text-sm text-[#5C7063]">High Risk</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-600 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                {analytics?.compliance_breakdown?.Unchecked || 0}
              </div>
              <p className="text-sm text-[#5C7063]">Not Checked</p>
            </div>
          </div>
        </motion.div>

        {/* Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10"
        >
          <h2 className="text-2xl font-bold text-[#1C2A21] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Insights & Recommendations
          </h2>
          <div className="space-y-4">
            {analytics?.scripts_this_month >= 4 ? (
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-semibold text-green-900">Great Progress!</p>
                  <p className="text-sm text-green-700">
                    You've created {analytics?.scripts_this_month} scripts this month. Keep up the consistent content creation!
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-semibold text-yellow-900">Increase Content Output</p>
                  <p className="text-sm text-yellow-700">
                    You've created {analytics?.scripts_this_month} scripts this month. Try to aim for at least 4 scripts for consistent growth.
                  </p>
                </div>
              </div>
            )}

            {(analytics?.compliance_breakdown?.High > 0 || analytics?.compliance_breakdown?.Medium > 0) && (
              <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-semibold text-red-900">Compliance Alert</p>
                  <p className="text-sm text-red-700">
                    You have {(analytics?.compliance_breakdown?.High || 0) + (analytics?.compliance_breakdown?.Medium || 0)} scripts with compliance concerns. 
                    Review and fix them before posting.
                  </p>
                </div>
              </div>
            )}

            {analytics?.scheduled_posts === 0 && (
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
                <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-semibold text-blue-900">Plan Your Content</p>
                  <p className="text-sm text-blue-700">
                    No posts scheduled yet. Use the Content Calendar to plan your content distribution strategy.
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3 p-4 bg-[#F4F1EA] rounded-xl">
              <TrendingUp className="w-5 h-5 text-[#2C5F44] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-semibold text-[#1C2A21]">Content Ideas Generated</p>
                <p className="text-sm text-[#5C7063]">
                  You've generated {analytics?.total_ideas || 0} content ideas. Turn more ideas into scripts to grow your content library.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="mt-8 flex gap-4"
        >
          <Button
            onClick={() => navigate(`/script/${doctorId}`)}
            className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full flex-1"
            data-testid="analytics-create-script-btn"
          >
            Create New Script
          </Button>
          <Button
            onClick={() => navigate(`/calendar/${doctorId}`)}
            variant="outline"
            className="rounded-full border-[#2C5F44]/20 flex-1"
            data-testid="analytics-calendar-btn"
          >
            View Calendar
          </Button>
        </motion.div>
      </div>
    </div>
  );
}